'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  emitNotification,
  dismissNotification,
  clearAllNotifications,
  markNotificationRead,
  markNotificationUnread,
  markAllNotificationsRead,
  getNotifications,
  subscribeNotifications,
} from '@lib/notifications'
import type { Notification, NotificationPayload } from '@lib/notifications'

export type { Notification, NotificationPayload, NotificationType, NotificationAction } from '@lib/notifications'

interface UseNotificationsReturn {
  notifications: Notification[]
  unreadCount: number
  emit: (payload: NotificationPayload) => Promise<void>
  dismiss: (id: string) => Promise<void>
  clearAll: () => Promise<void>
  markRead: (id: string) => Promise<void>
  markUnread: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([])

  useEffect(() => {
    let mounted = true

    const load = async () => {
      const data = await getNotifications()
      if (mounted) {
        setNotifications(data)
      }
    }

    load()

    const unsubscribe = subscribeNotifications({
      onNotification: (n) => {
        if (!mounted) return
        setNotifications((prev) => {
          const filtered = prev.filter((p) => p.id !== n.id)

          return [n, ...filtered]
        })
      },
      onDismiss: (id) => {
        if (!mounted) return
        setNotifications((prev) => prev.filter((p) => p.id !== id))
      },
      onClear: () => {
        if (!mounted) return
        setNotifications([])
      },
      onMarkRead: (id, readAt) => {
        if (!mounted) return
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, readAt } : n)))
      },
      onMarkUnread: (id) => {
        if (!mounted) return
        setNotifications((prev) =>
          prev.map((n) => {
            if (n.id !== id) return n
            const { readAt: _, ...rest } = n

            return rest
          }),
        )
      },
      onMarkAllRead: (readAt) => {
        if (!mounted) return
        setNotifications((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt })))
      },
    })

    return () => {
      mounted = false
      unsubscribe()
    }
  }, [])

  const emit = useCallback(async (payload: NotificationPayload) => {
    await emitNotification(payload)
  }, [])

  const dismiss = useCallback(async (id: string) => {
    await dismissNotification(id)
  }, [])

  const clearAll = useCallback(async () => {
    await clearAllNotifications()
  }, [])

  const markRead = useCallback(async (id: string) => {
    await markNotificationRead(id)
  }, [])

  const markUnread = useCallback(async (id: string) => {
    await markNotificationUnread(id)
  }, [])

  const markAllRead = useCallback(async () => {
    await markAllNotificationsRead()
  }, [])

  const unreadCount = useMemo(() => notifications.filter((n) => !n.readAt).length, [notifications])

  return {
    notifications,
    unreadCount,
    emit,
    dismiss,
    clearAll,
    markRead,
    markUnread,
    markAllRead,
  }
}
